/**
 * O editor gráfico de um componente do diagrama.
 *
 * **É o `EditorComponenteGrafico` do Sistema de hoje**, que nunca foi arrastar e
 * soltar: é o componente desenhado com a grade de 1 passo ao fundo, as medidas ao
 * lado — passo em px e mm, largura, altura, e a altura de cada pino — e os
 * formulários que gravam. Aqui os formulários ficam ao lado do traço em vez de
 * atrás de um modal, e o desenho se refaz sozinho a cada gravação.
 *
 * **A unidade é o passo**, 1,5 mm. Altura e deslocamento são digitados em passos;
 * o px aparece junto só para casar o número com o que o olho vê na grade.
 *
 * **Tela própria, e não mais uma aba da ficha.** O editor quer o desenho inteiro
 * à esquerda e campos à direita; a ficha do diagrama já está espremida com seis
 * abas.
 *
 * **Gravar é imediato, como no legado** — e a coluna `svg` do diagrama só muda
 * quando alguém redesenhar, então o desenho publicado não se mexe por conta
 * disto. A faixa no topo diz isso, porque é a diferença que confunde.
 */

import { useEffect, useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import {
  RAMIFICACOES, usarCandidatosDaLigacao, usarDesenhoDoComponente, usarEditarLigacao,
  type LigacaoParaGravar,
} from "@/api/editorDoComponente"
import {
  comCor, fichaDe, usarComponentesDoDiagrama, usarEditarComponenteDoDiagrama,
  type FichaDoComponente,
} from "@/api/desenhoDoDiagrama"
import { usarTerminaisDoComponente, type TerminalDoComponente } from "@/api/terminaisDoComponente"
import { DesenhoSvg } from "@/kit/DesenhoSvg"
import { SeletorDeCorDeFuncao } from "./SeletorDeCorDeFuncao"
import { SeletorDeDesenhoInterno } from "./SeletorDeDesenhoInterno"
import { SeletorDeTipo } from "./TipoDoComponente"
import { Rotulado } from "./Rotulado"
import { ErroDaApi, ErroDeTrava } from "@/api/erros"

function mensagem(e: unknown): string {
  if (e instanceof ErroDeTrava) return e.message
  if (e instanceof ErroDaApi) return e.message
  return e instanceof Error ? e.message : "Não foi possível gravar."
}

const entrada: React.CSSProperties = {
  padding: "4px 7px", fontSize: 11.5, border: "1px solid var(--line)",
  borderRadius: 5, background: "var(--surface)", color: "var(--txt)",
}

function campo(rotulo: string, filho: React.ReactNode, dica?: string) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 3 }} title={dica}>
      <span style={{ fontSize: 10, color: "var(--txt3)", textTransform: "uppercase",
                     letterSpacing: 0.4 }}>{rotulo}</span>
      {filho}
    </label>
  )
}

export function EditorDoComponente() {
  const { projetoId: p, diagramaId: d, componenteId: c } = useParams()
  const projetoId = Number(p)
  const diagramaId = Number(d)
  const componenteId = Number(c)
  const navegar = useNavigate()

  const [grid, setGrid] = useState(true)
  const [zoom, setZoom] = useState(1.5)
  const [recado, setRecado] = useState<string | null>(null)

  const desenho = usarDesenhoDoComponente(componenteId, grid)
  const componentes = usarComponentesDoDiagrama(diagramaId)
  const terminais = usarTerminaisDoComponente(componenteId)
  const salvarFicha = usarEditarComponenteDoDiagrama(projetoId, diagramaId)

  const linha = useMemo(
    () => (componentes.data ?? []).find((x) => x.id === componenteId),
    [componentes.data, componenteId])

  const [ficha, setFicha] = useState<FichaDoComponente | null>(null)
  useEffect(() => { if (linha) setFicha(fichaDe(linha)) }, [linha])

  const [aberto, setAberto] = useState<number>(0)

  return (
    <section style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "9px 14px",
                    borderBottom: "1px solid var(--line2)", flex: "0 0 auto" }}>
        <button onClick={() => navegar(`/projetos/${projetoId}/diagramas`)}
                style={{ ...entrada, cursor: "pointer" }}>← Diagramas</button>
        <b style={{ fontSize: 13 }}>
          {desenho.data
            ? `${desenho.data.componenteCodigo} ${desenho.data.componenteDescricao}`
            : `Componente ${componenteId}`}
        </b>
        <span style={{ fontSize: 11, color: "var(--txt3)" }}>
          diagrama {diagramaId} · coluna {desenho.data?.posicao || linha?.posicao || "—"}
        </span>
      </div>

      <div className="barra" style={{
        flex: "0 0 auto", padding: "7px 14px", fontSize: 11.5, lineHeight: 1.5,
        background: "var(--acao-soft)", color: "var(--acao-on-soft)",
        borderBottom: "1px solid var(--acao)",
      }}>
        <b>Aqui grava na hora.</b>{" "}
        <span style={{ opacity: 0.9 }}>
          O que muda é o cadastro do componente; o desenho que o cliente vê só muda quando alguém
          apertar <b>Redesenhar</b> na ficha do diagrama.
        </span>
      </div>

      {recado && (
        <div style={{ flex: "0 0 auto", padding: "7px 14px", fontSize: 11.5,
                      background: "var(--surface2)", borderBottom: "1px solid var(--line2)" }}>
          {recado}
        </div>
      )}

      <div style={{ flex: 1, minHeight: 0, display: "flex", flexWrap: "wrap", overflow: "auto" }}>
        {/* ---------------- o traço ---------------- */}
        <div style={{ flex: "1 1 46%", minWidth: 330, display: "flex", flexDirection: "column",
                      borderRight: "1px solid var(--line2)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "7px 12px",
                        borderBottom: "1px solid var(--line2)", flex: "0 0 auto" }}>
            <label style={{ fontSize: 10.5, color: "var(--txt2)", display: "flex",
                            alignItems: "center", gap: 5 }}>
              <input type="checkbox" checked={grid} onChange={(e) => setGrid(e.target.checked)} />
              grade de 1 passo
            </label>
            <label style={{ fontSize: 10.5, color: "var(--txt2)", display: "flex",
                            alignItems: "center", gap: 6 }}>
              zoom
              <input type="range" min={0.5} max={4} step={0.25} value={zoom}
                     onChange={(e) => setZoom(Number(e.target.value))} style={{ width: 90 }} />
              <span style={{ fontVariantNumeric: "tabular-nums" }}>{zoom.toFixed(1)}×</span>
            </label>
            <button onClick={() => desenho.refetch()} disabled={desenho.isFetching}
                    style={{ ...entrada, cursor: "pointer", marginLeft: "auto" }}>
              {desenho.isFetching ? "Desenhando…" : "Redesenhar este"}
            </button>
          </div>

          <div style={{ flex: 1, minHeight: 0, overflow: "auto", padding: 12,
                        display: "flex", justifyContent: "center", alignItems: "flex-start" }}>
            {desenho.isPending ? (
              <span style={{ fontSize: 12, color: "var(--txt3)" }}>Desenhando o componente…</span>
            ) : desenho.error ? (
              <span style={{ fontSize: 12, color: "var(--erro)" }}>{mensagem(desenho.error)}</span>
            ) : desenho.data ? (
              <DesenhoSvg svg={desenho.data.svg} largura={desenho.data.svgLarguraPx} zoom={zoom}
                          rotulo={`Componente ${desenho.data.componenteCodigo}`} />
            ) : null}
          </div>

          {desenho.data && (
            <div style={{ flex: "0 0 auto", borderTop: "1px solid var(--line2)", padding: "8px 12px",
                          fontSize: 11, color: "var(--txt2)", maxHeight: "34%", overflow: "auto" }}>
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 6 }}>
                <span>passo <b>{desenho.data.passoPx.toFixed(2)} px</b> = {desenho.data.passoMm.toFixed(2)} mm</span>
                <span>largura <b>{desenho.data.larguraPassos}</b> passos = {desenho.data.larguraPx.toFixed(1)} px</span>
                <span>altura <b>{desenho.data.alturaPassos}</b> passos = {desenho.data.alturaPx.toFixed(1)} px</span>
              </div>
              <table style={{ borderCollapse: "collapse", width: "100%",
                              fontVariantNumeric: "tabular-nums" }}>
                <tbody>
                  {desenho.data.terminais.map((t) => (
                    <tr key={t.diagramaLigacaoId}
                        onClick={() => setAberto(t.diagramaLigacaoId)}
                        style={{ cursor: "pointer",
                                 background: aberto === t.diagramaLigacaoId ? "var(--surface2)" : undefined }}>
                      <td style={{ padding: "2px 4px" }}>pino <b>{t.pino || "—"}</b></td>
                      <td style={{ padding: "2px 4px", color: "var(--txt3)" }}>{t.posicao}</td>
                      <td style={{ padding: "2px 4px", textAlign: "right" }}>{t.passos} passos</td>
                      <td style={{ padding: "2px 4px", textAlign: "right", color: "var(--txt3)" }}>
                        {t.px.toFixed(1)} px
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ marginTop: 6, fontSize: 10, color: "var(--txt3)" }}>
                desenhado em {((desenho.data.milissegundosBusca + desenho.data.milissegundosDesenho
                  + desenho.data.milissegundosEstilo) / 1000).toFixed(1)} s
              </div>
            </div>
          )}
        </div>

        {/* ---------------- os campos ---------------- */}
        <div style={{ flex: "1 1 54%", minWidth: 340, overflow: "auto", padding: "10px 14px" }}>
          <b style={{ fontSize: 12 }}>Formatar componente</b>
          {ficha ? (
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end",
                          marginTop: 8, marginBottom: 6 }}>
              {campo("Título", (
                <input value={ficha.titulo} onChange={(e) => setFicha({ ...ficha, titulo: e.target.value })}
                       style={{ ...entrada, width: 160 }} />
              ))}
              {campo("Texto", (
                <input value={ficha.texto} onChange={(e) => setFicha({ ...ficha, texto: e.target.value })}
                       style={{ ...entrada, width: 160 }} />
              ))}
              {campo("Tipo", (
                <SeletorDeTipo valor={ficha.tipo} estilo={{ ...entrada, width: 190 }}
                               aoMudar={(t) => setFicha({ ...ficha, tipo: t })} />
              ))}
              {campo("Altura", (
                <input type="number" value={ficha.altura}
                       onChange={(e) => setFicha({ ...ficha, altura: Number(e.target.value) })}
                       style={{ ...entrada, width: 60 }} />
              ), "Em passos, somada à altura que os terminais pedem.")}
              {campo("Margem abaixo", (
                <input type="number" value={ficha.marginBottom}
                       onChange={(e) => setFicha({ ...ficha, marginBottom: Number(e.target.value) })}
                       style={{ ...entrada, width: 60 }} />
              ), "Em passos, entre este componente e o de baixo.")}
              {campo("Texto do pino", (
                <input type="number" value={ficha.tamanhoTextoPino}
                       onChange={(e) => setFicha({ ...ficha, tamanhoTextoPino: Number(e.target.value) })}
                       style={{ ...entrada, width: 60 }} />
              ), "0 = o tamanho padrão.")}
              <div style={{ flexBasis: "100%" }}>
                <Rotulado rotulo="Cor">
                  <SeletorDeCorDeFuncao valor={ficha.componenteCor}
                                        aoMudar={(c) => setFicha(comCor(ficha, c, linha?.posicao))} />
                </Rotulado>
              </div>
              <div style={{ flexBasis: "100%" }}>
                <Rotulado rotulo="Desenho interno">
                  <SeletorDeDesenhoInterno
                    posicao={linha?.posicao}
                    valor={ficha.diagramaDesenhoInternoId}
                    aoMudar={(id) => setFicha({ ...ficha, diagramaDesenhoInternoId: id })} />
                </Rotulado>
              </div>
              <label style={{ fontSize: 10.5, color: "var(--txt2)", display: "flex",
                              alignItems: "center", gap: 5 }}>
                <input type="checkbox" checked={ficha.ocultar}
                       onChange={(e) => setFicha({ ...ficha, ocultar: e.target.checked })} />
                ocultar
              </label>
              <button
                onClick={() => {
                  setRecado(null)
                  salvarFicha.mutate({ id: componenteId, ficha }, {
                    onSuccess: () => { setRecado("Componente gravado."); desenho.refetch() },
                    onError: (e) => setRecado(mensagem(e)),
                  })
                }}
                disabled={salvarFicha.isPending}
                style={{ ...entrada, cursor: "pointer", fontWeight: 600 }}>
                {salvarFicha.isPending ? "Gravando…" : "Gravar componente"}
              </button>
            </div>
          ) : componentes.isPending ? (
            <p style={{ fontSize: 11.5, color: "var(--txt3)" }}>Carregando a ficha…</p>
          ) : (
            <p style={{ fontSize: 11.5, color: "var(--txt3)" }}>
              Este componente não está na lista do diagrama {diagramaId}.
            </p>
          )}

          <b style={{ fontSize: 12 }}>Ligações</b>
          <p style={{ fontSize: 10.5, color: "var(--txt3)", margin: "3px 0 8px" }}>
            Uma por terminal. Clique no pino — na tabela de medidas ou aqui — para abrir.
          </p>

          {terminais.isPending ? (
            <p style={{ fontSize: 11.5, color: "var(--txt3)" }}>Carregando as ligações…</p>
          ) : (terminais.data ?? []).length === 0 ? (
            <p style={{ fontSize: 11.5, color: "var(--txt3)" }}>Este componente não tem ligação.</p>
          ) : (
            (terminais.data ?? []).map((t) => (
              <FormularioDaLigacao
                key={t.id} terminal={t} aberto={aberto === t.id}
                aoAbrir={() => setAberto(aberto === t.id ? 0 : t.id)}
                projetoId={projetoId} diagramaId={diagramaId} componenteId={componenteId}
                aoGravar={(texto) => { setRecado(texto); desenho.refetch() }}
              />
            ))
          )}
        </div>
      </div>
    </section>
  )
}

/**
 * O `DiagramaLigacaoEditar` do legado, com os mesmos campos e a mesma ordem.
 *
 * Os dois selects de lista — ramificação e junção — só buscam quando abertos: são
 * todos os terminais do diagrama no mesmo circuito, e no legado eles também
 * ficam atrás de um botão "Carregar".
 */
function FormularioDaLigacao({
  terminal, aberto, aoAbrir, projetoId, diagramaId, componenteId, aoGravar,
}: {
  terminal: TerminalDoComponente
  aberto: boolean
  aoAbrir: () => void
  projetoId: number
  diagramaId: number
  componenteId: number
  aoGravar: (recado: string) => void
}) {
  const gravar = usarEditarLigacao(projetoId, diagramaId, componenteId)
  const candidatos = usarCandidatosDaLigacao(terminal.id, aberto)

  const [corpo, setCorpo] = useState<LigacaoParaGravar>(() => daLinha(terminal))
  useEffect(() => { setCorpo(daLinha(terminal)) }, [terminal])

  const cabecalho = (
    <button onClick={aoAbrir} style={{
      display: "flex", alignItems: "center", gap: 8, width: "100%", textAlign: "left",
      padding: "6px 8px", fontSize: 11.5, cursor: "pointer",
      background: aberto ? "var(--surface2)" : "transparent",
      border: "1px solid var(--line2)", borderRadius: 5, color: "var(--txt)",
    }}>
      <b>pino {terminal.pino || "—"}</b>
      <span style={{ color: "var(--txt3)" }}>
        {terminal.codigoDoOutro} {terminal.descricaoDoOutro} · pino {terminal.pinoDaLigacao || "—"}
      </span>
      <span style={{ marginLeft: "auto", color: "var(--txt3)", fontSize: 10.5 }}>
        {terminal.altura !== 0 && `altura ${terminal.altura} · `}
        {terminal.ramificacao !== 0 && "ramifica · "}
        {terminal.juncao && "junção · "}
        {terminal.ocultar && "oculto · "}
        {aberto ? "▾" : "▸"}
      </span>
    </button>
  )

  if (!aberto) return <div style={{ marginBottom: 5 }}>{cabecalho}</div>

  return (
    <div style={{ marginBottom: 5 }}>
      {cabecalho}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end",
                    padding: "9px 8px 4px" }}>
        {campo("Texto do pino", (
          <input value={corpo.texto} onChange={(e) => setCorpo({ ...corpo, texto: e.target.value })}
                 style={{ ...entrada, width: 130 }} />
        ))}
        {campo("Altura (passos)", (
          <input type="number" value={corpo.altura}
                 onChange={(e) => setCorpo({ ...corpo, altura: Number(e.target.value) })}
                 style={{ ...entrada, width: 76 }} />
        ), "Desloca este terminal para cima ou para baixo, em passos de 1,5 mm.")}
        <label style={{ fontSize: 10.5, color: "var(--txt2)", display: "flex",
                        alignItems: "center", gap: 5 }}>
          <input type="checkbox" checked={corpo.deslocar}
                 onChange={(e) => setCorpo({ ...corpo, deslocar: e.target.checked })} />
          deslocar o texto interno
        </label>
        {campo("Texto na coluna do nome", (
          <input value={corpo.textoDescricao}
                 onChange={(e) => setCorpo({ ...corpo, textoDescricao: e.target.value })}
                 style={{ ...entrada, width: 180 }} />
        ))}
      </div>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end",
                    padding: "0 8px 4px" }}>
        {campo("Ramificação", (
          <select value={corpo.ramificacao}
                  onChange={(e) => setCorpo({ ...corpo, ramificacao: Number(e.target.value) })}
                  style={{ ...entrada, width: 210 }}>
            {RAMIFICACOES.map(([v, nome]) => <option key={v} value={v}>{nome}</option>)}
          </select>
        ))}
        {corpo.ramificacao !== 0 && campo("Posição", (
          <select value={corpo.ramificacaoPosicao}
                  onChange={(e) => setCorpo({ ...corpo, ramificacaoPosicao: Number(e.target.value) })}
                  style={{ ...entrada, width: 90 }}>
            <option value={0}>—</option>
            {[1, 2, 3, 4, 5, 6].map((n) => <option key={n} value={n}>Posição {n}</option>)}
          </select>
        ))}
        {corpo.ramificacao !== 0 && campo("De qual terminal", (
          <select value={corpo.ramificacaoId}
                  onChange={(e) => setCorpo({ ...corpo, ramificacaoId: Number(e.target.value) })}
                  style={{ ...entrada, width: 300 }}>
            <option value={0}>{candidatos.isPending ? "carregando…" : "—"}</option>
            {(candidatos.data ?? []).map((k) => (
              <option key={k.diagramaLigacaoId} value={k.diagramaLigacaoId}>
                {k.componenteCodigo} {k.componenteDescricao} · pino {k.pino || "—"} · {k.circuitoDescricao}
              </option>
            ))}
          </select>
        ))}
      </div>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end",
                    padding: "0 8px 8px" }}>
        <label style={{ fontSize: 10.5, color: "var(--txt2)", display: "flex",
                        alignItems: "center", gap: 5 }}>
          <input type="checkbox" checked={corpo.ocultar}
                 onChange={(e) => setCorpo({ ...corpo, ocultar: e.target.checked })} />
          ocultar
        </label>
        <label style={{ fontSize: 10.5, color: "var(--txt2)", display: "flex",
                        alignItems: "center", gap: 5 }}
               title="Uma junção oculta automaticamente a ligação de destino.">
          <input type="checkbox" checked={corpo.juncao}
                 onChange={(e) => setCorpo({ ...corpo, juncao: e.target.checked })} />
          junção
        </label>
        {corpo.juncao && campo("Junta com", (
          <select
            value={`${corpo.juncaoDiagramaComponenteId}-${corpo.juncaoPinoutId}`}
            onChange={(e) => {
              const [dc, pin] = e.target.value.split("-")
              setCorpo({
                ...corpo,
                juncaoDiagramaComponenteId: Number(dc),
                juncaoPinoutId: Number(pin),
              })
            }}
            style={{ ...entrada, width: 320 }}>
            <option value="0-0">{candidatos.isPending ? "carregando…" : "—"}</option>
            {(candidatos.data ?? []).map((k) => (
              <option key={k.diagramaLigacaoId}
                      value={`${k.diagramaComponenteId}-${k.pinoutId}`}>
                {k.componenteCodigo} {k.componenteDescricao} · pino {k.pino || "—"}
              </option>
            ))}
          </select>
        ))}
        <button
          onClick={() => gravar.mutate({ id: terminal.id, corpo }, {
            onSuccess: (r) => aoGravar(
              r.componentesParaAtualizar.length > 0
                ? `Ligação gravada. ${r.componentesParaAtualizar.length} componente vizinho também mudou.`
                : "Ligação gravada."),
            onError: (e) => aoGravar(mensagem(e)),
          })}
          disabled={gravar.isPending}
          style={{ ...entrada, cursor: "pointer", fontWeight: 600 }}>
          {gravar.isPending ? "Gravando…" : "Gravar ligação"}
        </button>
      </div>
    </div>
  )
}

/** Tudo o que o `PUT` substitui, lido da linha — inclusive o que a tela não mostra. */
function daLinha(t: TerminalDoComponente): LigacaoParaGravar {
  return {
    altura: t.altura,
    deslocar: t.deslocar,
    ramificacao: t.ramificacao,
    ramificacaoId: t.ramificacaoId,
    ramificacaoPosicao: t.ramificacaoPosicao,
    ocultar: t.ocultar,
    juncao: t.juncao,
    juncaoDiagramaComponenteId: t.juncaoDiagramaComponenteId,
    juncaoPinoutId: t.juncaoPinoutId,
    texto: t.texto,
    textoDescricao: t.textoDescricao,
  }
}
